import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import useFormHook from '@shared/hooks/useFormHook';
import { useEffect, useState } from 'react';
import { positionsService } from '../services/positions';
import { notifySuccess, notifyError } from '@config/tostifyConfig/tostifyConfig';
import LoadingButton from "@shared/components/LoadingButton"

export default function PositionRights({ onClose, positionId, position }) {
    const { modalRef } = useFormHook({}, onClose);
    const { t } = useTranslation();
    const [rights, setRights] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedRights, setSelectedRights] = useState([]);

    useEffect(() => {
        const fetchRights = async () => {
            try {
                setLoading(true);
                const rightsData = await positionsService.getJobRights();
                setRights(rightsData);
                
                if (positionId) {
                    try {
                        const currentJobRights = await positionsService.getJobRightsById(positionId);
                        const currentRightsIds = currentJobRights.hrJobRights?.map(right => right.id) || [];
                        setSelectedRights(currentRightsIds);
                    } catch (error) {
                        console.error('Error fetching current job rights:', error);
                        setSelectedRights([]);
                    }
                }
            } catch (error) {
                console.error('Error fetching rights:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchRights();
    }, [positionId]);

    const handleCheckboxChange = (rightId) => {
        setSelectedRights(prev => {
            if (prev.includes(rightId)) {
                return prev.filter(id => id !== rightId);
            } else {
                return [...prev, rightId];
            }
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setLoading(true);
            // Convert to array of numbers and filter out any undefined/null values
            const cleanArray = selectedRights.filter(id => id != null).map(id => Number(id));
            await positionsService.saveJobRights(positionId, JSON.stringify(cleanArray));
            notifySuccess(t('common.updatedSuccessfully'));
            onClose();
        } catch (error) {
            console.error('Error saving rights:', error);
            notifyError(t('common.errorMessage'));
        } finally {
            setLoading(false);
        }
    };

    // Map approval keys to translation keys
    const getTranslationKey = (approvalKey) => {
        const translationMap = {
            'PAID_LEAVE1': 'organizationalStructure.positions.rightsForm.firstSignOff',
            'PAID_LEAVE2': 'organizationalStructure.positions.rightsForm.secondSignOff2',
            'BT_REQUEST': 'organizationalStructure.positions.rightsForm.secondSignOff',
            'BT_REPORT': 'organizationalStructure.positions.rightsForm.travelReport',
            'EXTRA_HOURS': 'organizationalStructure.positions.rightsForm.addJobCode1',
            'SCHEDULE': 'organizationalStructure.positions.rightsForm.schedule',
            'MANAGER': 'organizationalStructure.positions.rightsForm.manager',
            'ACCESS_SALARY': 'organizationalStructure.positions.rightsForm.salaryInformation',
            'MANAGER_REPORTS': 'organizationalStructure.positions.rightsForm.managerReports',
            'HR_PAID_LEAVE': 'organizationalStructure.positions.rightsForm.hrConfirmationOfLeave',
            'HR_BUSINESS_TRIP': 'organizationalStructure.positions.rightsForm.hrConfirmationOfTravel',
            'HR_EXTRA_HOURS': 'organizationalStructure.positions.rightsForm.hrConfirmationOfExtraWork',
            'HR_SCHEDULE': 'organizationalStructure.positions.rightsForm.hrConfirmationOfSchedule'
        };

        return translationMap[approvalKey] || approvalKey;
    };

    return (
        <div className="modal show" tabIndex="-1">
            <div className="modal-dialog modal-confirm" ref={modalRef}>
                <div className="modal-content">
                    <button type="button" className="close" onClick={onClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                    <div className="modal-body">
                        <h4 className="modal-title">{t('organizationalStructure.positions.rightsForm.title')}</h4>
                        <div className="flex-row mb-2 width-100">
                            <div className="flex-column width-30 gap-1">
                                <span className="span-bold"> {t('organizationalStructure.positions.rightsForm.positionCode')}</span>
                                <input type="text" name="position" id="position" value={position?.jobCode || ''} disabled />
                            </div>
                            <div className="flex-column width-50 gap-1">
                                <span className="span-bold"> {t('organizationalStructure.positions.rightsForm.positionTitle')}</span>
                                <input
                                    type="text"
                                    name="position"
                                    id="position"
                                    value={position?.longDescription || ''}
                                    disabled
                                />
                            </div>
                            <div className="flex-column width-30 gap-1">
                                <span className="span-bold"> {t('organizationalStructure.positions.rightsForm.noc')}</span>
                                <input type="text" name="position" id="position" value={position?.nkpd || ''} disabled />
                            </div>
                        </div>
                        <form id="rightsForm" className="region-form" onSubmit={handleSubmit}>
                            <div className="form-sections">
                                <div className="form-section">
                                    {rights.slice(0, 6).map((right) => (
                                        <div className="flex-row" key={right.id}>
                                            <input
                                                type="checkbox"
                                                name={`right_${right.id}`}
                                                id={`right_${right.id}`}
                                                checked={selectedRights.includes(right.id)}
                                                onChange={() => handleCheckboxChange(right.id)}
                                            />
                                            <span className="span-bold">
                                                {t(getTranslationKey(right.approvalKey))}
                                            </span>
                                        </div>
                                    ))}
                                </div>

                                <div className="form-section">
                                    {rights.slice(6).map((right) => (
                                        <div className="flex-row" key={right.id}>
                                            <input
                                                type="checkbox"
                                                name={`right_${right.id}`}
                                                id={`right_${right.id}`}
                                                checked={selectedRights.includes(right.id)}
                                                onChange={() => handleCheckboxChange(right.id)}
                                            />
                                            <span className="span-bold">
                                                {t(getTranslationKey(right.approvalKey))}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="modal-footer">
                                <LoadingButton type="submit" className="form-btn btn-blue" loading={loading}>{t('common.save')}</LoadingButton>
                                <button type="button" className="form-btn btn-red" onClick={onClose}>
                                    {t('common.cancel')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
}